import { isWithinInterval, parseJSON } from "date-fns";
import {
  CashFlowAndPerformanceStatisticsList,
  DateValuePairList,
} from "@rentablo/api/apiv1";
import { convertBalancesToChartData } from "@helpers/charts";
import { round } from "@helpers/math";
import { ExtendedAccount } from "@features/depots/types";

export type AccountPerformance = {
  portfolioValue: number;
  chartData: { date: Date; value: number }[];
  sinceYear?: number;
  changeAbsCashFlowAdjusted?: number;
  nativePortfolioValue?: number;
  isFetching: boolean;
};

export const isCashLikeAccount = (account: ExtendedAccount): boolean =>
  account.typeFromBank === "CREDIT_CARD" ||
  account.typeFromBank === "REFERENCE_ACCOUNT" ||
  account.type === "02_cash" ||
  (account.depotIds?.length ?? 0) > 0;

// Last valid point of the full series, so accounts without recent
// bookings still show their balance.
export const lastBalanceValue = (data?: DateValuePairList): number => {
  const lastPoint = data?.dateValuePairs?.findLast(
    ({ date, value }) => date !== undefined && value !== undefined
  );
  return lastPoint?.value === undefined ? 0 : round(lastPoint.value, 2);
};

export const lastPerformanceValue = (
  data?: CashFlowAndPerformanceStatisticsList
): number => {
  const [statistic] = data?.cashFlowAndPerformanceStatisticsList ?? [];
  const lastPoint = statistic?.twrorResults?.findLast(
    ({ date, portfolioValue }) =>
      date !== undefined && portfolioValue !== undefined
  );
  return lastPoint?.portfolioValue === undefined
    ? 0
    : round(lastPoint.portfolioValue, 2);
};

export const projectBalances = (
  data: DateValuePairList | undefined,
  startDate: Date,
  endDate: Date
) => {
  const chartData = convertBalancesToChartData(data?.dateValuePairs).filter(
    ({ date }) => isWithinInterval(date, { start: startDate, end: endDate })
  );

  return {
    portfolioValue: lastBalanceValue(data),
    chartData,
  };
};

export const projectPerformanceStatistic = (
  data: CashFlowAndPerformanceStatisticsList | undefined
) => {
  const [statistic] = data?.cashFlowAndPerformanceStatisticsList ?? [];

  const chartData = (statistic?.twrorResults ?? [])
    .filter(({ date, portfolioValue }) => date && portfolioValue !== undefined)
    .map(({ date, portfolioValue }) => ({
      date: parseJSON(date!),
      value: round(portfolioValue!, 2),
    }));

  const lastPoint = chartData[chartData.length - 1];
  const firstPoint = chartData[0];

  return {
    portfolioValue: lastPoint?.value ?? 0,
    chartData,
    sinceYear: firstPoint?.date.getFullYear(),
    changeAbsCashFlowAdjusted:
      statistic?.changeTotal?.changeAbsCashFlowAdjusted,
  };
};
